import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, AlertTriangle, HardHat } from 'lucide-react';
import { format, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { AffectationConflict } from '@/types/conflits-absence';
import { useModalProtection } from '@/hooks/useModalProtection';

interface Membre {
  id: string;
  prenom: string;
  nom: string;
  statut: string;
  date_fin_statut?: string | null;
}

interface ModalDateFinStatutProps {
  isOpen: boolean;
  onClose: () => void;
  membre: Membre | null;
  nouveauStatut: 'repos' | 'en_arret';
  onConfirm: (dateFinStatut: string) => Promise<void>;
}

const STATUT_LABELS: Record<string, string> = {
  repos: 'en repos',
  en_arret: 'en arrêt',
};

const STATUT_EMOJI: Record<string, string> = {
  repos: '😴',
  en_arret: '🏥',
};

export function ModalDateFinStatut({
  isOpen,
  onClose,
  membre,
  nouveauStatut,
  onConfirm,
}: ModalDateFinStatutProps) {
  useModalProtection(isOpen);
  const [isLoading, setIsLoading] = useState(false);
  // Par défaut, date de demain
  const [dateFinStatut, setDateFinStatut] = useState<Date>(addDays(new Date(), 1));
  const [calendarOpen, setCalendarOpen] = useState(false);

  // État pour les conflits d'affectation
  const [affectationConflicts, setAffectationConflicts] = useState<AffectationConflict[]>([]);
  const [isCheckingAffectations, setIsCheckingAffectations] = useState(false);

  // Vérifier les affectations existantes quand la date change
  useEffect(() => {
    const checkAffectations = async () => {
      if (!membre?.id || !dateFinStatut || !isOpen) {
        setAffectationConflicts([]);
        return;
      }

      setIsCheckingAffectations(true);
      try {
        const today = format(new Date(), 'yyyy-MM-dd');
        const dateFin = format(dateFinStatut, 'yyyy-MM-dd');

        const { data, error } = await supabase.rpc('check_membre_affectations', {
          p_membre_id: membre.id,
          p_date_debut: today,
          p_date_fin: dateFin,
        });

        if (error) {
          console.error('Erreur vérification affectations:', error);
          setAffectationConflicts([]);
        } else {
          setAffectationConflicts(data || []);
        }
      } catch (err) {
        console.error('Exception vérification affectations:', err);
        setAffectationConflicts([]);
      } finally {
        setIsCheckingAffectations(false);
      }
    };

    checkAffectations();
  }, [membre?.id, dateFinStatut, isOpen]);

  const handleConfirm = async () => {
    if (!membre || !dateFinStatut) return;

    setIsLoading(true);
    try {
      const dateStr = format(dateFinStatut, 'yyyy-MM-dd');
      await onConfirm(dateStr);
      onClose();
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!membre) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {STATUT_EMOJI[nouveauStatut]} Définir la date de fin
          </DialogTitle>
          <DialogDescription>
            <span className="font-medium text-foreground">{membre.prenom} {membre.nom}</span> sera {STATUT_LABELS[nouveauStatut]} jusqu'à la date sélectionnée.
            <br />
            <span className="text-muted-foreground text-sm">
              Il repassera automatiquement en "disponible" à cette date.
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Sélection de la date */}
          <div className="space-y-2">
            <Label htmlFor="date-fin-statut">Date de retour prévue</Label>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  id="date-fin-statut"
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !dateFinStatut && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateFinStatut ? (
                    format(dateFinStatut, "EEEE d MMMM yyyy", { locale: fr })
                  ) : (
                    "Sélectionner une date"
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 z-50" align="start">
                <Calendar
                  mode="single"
                  selected={dateFinStatut}
                  onSelect={(date) => {
                    if (date) {
                      setDateFinStatut(date);
                      setCalendarOpen(false);
                    }
                  }}
                  disabled={(date) => date < new Date()}
                  initialFocus
                  locale={fr}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Info box */}
          <div className="bg-muted/50 rounded-lg p-3 text-sm">
            <div className="flex items-start gap-2">
              <CalendarIcon className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <div>
                <p className="text-muted-foreground">
                  Le <strong>{format(dateFinStatut, "d MMMM yyyy", { locale: fr })}</strong>, le statut de {membre.prenom} passera automatiquement à "disponible".
                </p>
              </div>
            </div>
          </div>

          {/* Vérification en cours */}
          {isCheckingAffectations && (
            <div className="flex items-center gap-2 p-3 rounded-lg border border-blue-500/30 bg-blue-500/5">
              <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
              <span className="text-sm text-blue-600">Vérification des affectations...</span>
            </div>
          )}

          {/* Avertissement conflits d'affectation */}
          {affectationConflicts.length > 0 && !isCheckingAffectations && (
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-medium text-orange-700 text-sm">
                    Ce membre est affecté sur {affectationConflicts.length} chantier{affectationConflicts.length > 1 ? 's' : ''} pendant cette période
                  </p>
                  <div className="mt-2 space-y-2">
                    {affectationConflicts.map((aff) => (
                      <div
                        key={aff.affectation_id}
                        className="flex items-center gap-2 p-2 rounded bg-orange-500/10"
                      >
                        <HardHat className="h-4 w-4 text-orange-600 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            {aff.chantier_nom}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {aff.jours_conflit} jour{aff.jours_conflit > 1 ? 's' : ''} impacté{aff.jours_conflit > 1 ? 's' : ''}
                            {' • '}
                            {format(new Date(aff.date_debut), 'dd/MM', { locale: fr })} → {format(new Date(aff.date_fin), 'dd/MM', { locale: fr })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Les métriques de ces chantiers pourraient être impactées par cette absence.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isLoading || !dateFinStatut}
          >
            {isLoading ? 'En cours...' : `Mettre ${STATUT_LABELS[nouveauStatut]}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Modal de confirmation pour changement de statut avant la date prévue
interface ModalConfirmChangementAnticipeProps {
  isOpen: boolean;
  onClose: () => void;
  membre: Membre | null;
  ancienStatut: string;
  nouveauStatut: string;
  dateFinPrevue: string;
  onConfirm: () => Promise<void>;
}

export function ModalConfirmChangementAnticipe({
  isOpen,
  onClose,
  membre,
  ancienStatut,
  nouveauStatut,
  dateFinPrevue,
  onConfirm,
}: ModalConfirmChangementAnticipeProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!membre) return null;

  const dateFinFormatted = format(new Date(dateFinPrevue), "d MMMM yyyy", { locale: fr });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-warning">
            <AlertTriangle className="h-5 w-5" />
            Changement anticipé
          </DialogTitle>
          <DialogDescription>
            <span className="font-medium text-foreground">{membre.prenom} {membre.nom}</span> était prévu {STATUT_LABELS[ancienStatut] || ancienStatut} jusqu'au <strong>{dateFinFormatted}</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="bg-warning/10 border border-warning/20 rounded-lg p-3 text-sm">
            <p className="text-foreground">
              Voulez-vous vraiment changer son statut maintenant ?
            </p>
            <p className="text-muted-foreground mt-1">
              La date de retour prévue sera annulée.
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Button
            variant="default"
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? 'En cours...' : 'Confirmer le changement'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
